import Card from "@material-ui/core/Card";
import Grid from "@material-ui/core/Grid";
import { useTranslation } from "react-i18next";
import "./css/payment.scss";
import { Button, CircularProgress, Typography } from "@material-ui/core";
import { useEffect, useState } from "react";
import { useFetch } from "@common/hooks/useFetch";
import { getLanguage } from "@utils/changeLanguage";
import { GET_DEFAULT_CARD_ROUTE, paymentRoute, paymentRouteCrossSell } from "@api-routes";
import "./Payment.css";
import { EPAYMENT_TYPES } from "@constants";
import { HandleErrorAndLoading } from "@common/handleErrorAndLoading/HandleErrorAndLoading";
import { saveAnonymous } from "@utils/storage";

interface ICardPaymentParams {
    isCrossSell: boolean;
}

interface IRegisterOrderResponse {
    success: boolean;
    orderId: string;
    formUrl: string;
}

interface IDefaultCard {
    cardholderName: string;
    default: boolean;
    expirationDate: string;
    partialDigits: string;
    paymentMethodId: number;
    paymentMethodType: EPAYMENT_TYPES;
    type: string;
    bindingId: string;
}

export const Payment = (props: ICardPaymentParams) => {
    const { isCrossSell } = props;
    const [getStatus, { get }] = useFetch();
    const { t } = useTranslation(["summary"]);
    const [orderId, setOrderId] = useState<string>("");
    const [jccForm, setJccForm] = useState<any>();
    const [paymentLoading, setPaymentLoading] = useState<boolean>(false);
    const [showPaymentFailedError, setShowPaymentFailedError] = useState<boolean>(false);
    const [isCardHolderDisabled, setIsCardHolderDisabled] = useState<boolean>(false);
    const [bindingCardholder, setBindingCardholder] = useState<string>("");
    const lang = getLanguage();

    const killJCCFunc = (jcc: any) => {
        console.log("jcc form...", jcc);
        jcc.destroy();
        console.log("killing jcc form...");
    };

    useEffect(() => {
        let jcc: any = null;

        const getDefaultPaymentCardAndInitializeForm = async () => {
            get<IDefaultCard>(GET_DEFAULT_CARD_ROUTE).then((response) => {
                if (response && response.bindingId) {
                    getOrderIdAndInitializeForm(response.bindingId);
                }
            });
        };

        const getOrderIdAndInitializeForm = async (bindingId: string) => {
            get<IRegisterOrderResponse>(isCrossSell ? paymentRouteCrossSell(lang) : paymentRoute(lang)).then(
                (response) => {
                    if (response && response.success) {
                        setOrderId(response.orderId);
                        initializeForm(response.orderId, bindingId);
                    }
                }
            );
        };

        const initializeForm = async (orderId: string, bindId: string) => {
            jcc = new (window as any).PaymentForm({
                mdOrder: orderId,
                onFormValidate: (isValid: boolean) => {},
                language: lang,
                containerClassName: "field-container",
                autoFocus: true,
                showPanIcon: true,
                panIconStyle: {
                    height: "16px",
                    top: "calc(50% - 8px)",
                    right: "8px",
                },
                fields: {
                    pan: {
                        container: document.querySelector("#pan"),
                    },
                    expiry: {
                        container: document.querySelector("#expiry"),
                    },
                    cvc: {
                        container: document.querySelector("#cvc"),
                    },
                },
                styles: {
                    base: {
                        color: "black",
                        fontSize: "18px",
                    },
                    valid: { color: "#4a148c", fontWeight: "bold" },
                    invalid: {
                        color: "red",
                        fontWeight: "bold",
                    },
                    placeholder: {
                        base: {
                            color: "gray",
                        },
                        focus: {
                            color: "transparent",
                        },
                    },
                },
            });

            isCrossSell
                ? jcc
                      .init()
                      .then(({ orderSession }: any) => {
                          const bindingInfo: any = orderSession.bindings.find((bind: any) => bind.id === bindId);
                          if (bindingInfo) {
                              (document.getElementById("select-binding") as any).options?.add(
                                  new Option(bindingInfo.pan, bindingInfo.id)
                              );
                              (document.getElementById("select-binding") as any).value = bindingInfo.id;
                              jcc.selectBinding(bindingInfo.id);
                              (document.getElementById("cardholderName") as any).value = bindingInfo.cardholderName;
                              setBindingCardholder(bindingInfo.cardholderName);
                              saveAnonymous(false);
                              setIsCardHolderDisabled(true);
                          }
                      })
                      .catch((e: any) => {
                          console.log("jcc form init error", e);
                      })
                : jcc
                      .init()
                      .then(() => {})
                      .catch((e: any) => {
                          console.log("jcc form init error", e);
                      });

            setJccForm(jcc);
        };

        saveAnonymous(true);
        isCrossSell ? getDefaultPaymentCardAndInitializeForm() : getOrderIdAndInitializeForm("");

        return () => {
            killJCCFunc(jcc);
        };
    }, []);

    const handlePayClick = async () => {
        if (!/(\w+-){3,10}\w+/g.test(orderId)) {
            console.log("order id: " + orderId + " is invalid, aborting payment");
            return;
        }

        setShowPaymentFailedError(false);
        setPaymentLoading(true);

        await jccForm
            .doPayment({
                cardholderName: (document.getElementById("cardholderName") as any).value,
                saveCard: isCrossSell ? false : true,
            })
            .then((result: any) => {
                console.log("doPayment result", result);
            })
            .catch((e: any) => {
                setShowPaymentFailedError(true);
                console.log("doPayment error", e);
            });

        setPaymentLoading(false);
    };

    return (
        <Card className="xtest_payment_iframe" elevation={2}>
            <HandleErrorAndLoading error={getStatus.error} isLoading={getStatus.isLoading}>
                <Grid container alignItems="center" justify="center">
                    <Grid
                        alignItems="center"
                        className="payment-container"
                        container
                        item
                        justify="center"
                        xs={12}
                        md={6}
                    >
                        <Grid container spacing={3} direction="row" style={{ padding: "30px 10px" }}>
                            <Grid item container alignItems="center" justify="center">
                                <Typography color="secondary" variant="h2">
                                    {t("summary:payment.paymentDetails")}
                                </Typography>
                            </Grid>
                            {isCrossSell && (
                                <Grid item xs={12}>
                                    <Typography color="secondary" variant="h5">
                                        {t("summary:payment.selectCard")}
                                    </Typography>
                                    <div id="select-binding-container">
                                        <select
                                            className="paymentTextInput"
                                            id="select-binding"
                                            aria-label="Default Card Select"
                                            onChange={(e: any) => {
                                                const binding = e.target.value;
                                                if (binding === "new_card") {
                                                    jccForm.selectBinding(null);
                                                    (document.getElementById("cardholderName") as any).value = "";
                                                    saveAnonymous(true);
                                                    setIsCardHolderDisabled(false);
                                                } else {
                                                    (document.getElementById(
                                                        "cardholderName"
                                                    ) as any).value = bindingCardholder;
                                                    jccForm.selectBinding(binding);
                                                    saveAnonymous(false);
                                                    setIsCardHolderDisabled(true);
                                                }
                                            }}
                                            defaultValue={"new_card"}
                                        >
                                            <option value="new_card">{t("summary:payment.newCard")}</option>
                                        </select>
                                    </div>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <Typography color="secondary" variant="h5">
                                    {t("summary:payment.cardHolder")}
                                </Typography>
                                <input
                                    type="text"
                                    id="cardholderName"
                                    placeholder={t("summary:payment.cardHolder")}
                                    className="paymentTextInput"
                                    disabled={isCardHolderDisabled}
                                ></input>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography color="secondary" variant="h5">
                                    {t("summary:payment.cardNumber")}
                                </Typography>
                                <div id="pan" className="paymentTextInput"></div>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography color="secondary" variant="h5">
                                    {t("summary:payment.expiryDate")}
                                </Typography>
                                <div id="expiry" className="paymentTextInput"></div>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography color="secondary" variant="h5">
                                    CVC / CVV
                                </Typography>
                                <div id="cvc" className="paymentTextInput"></div>
                            </Grid>
                            <Grid item container alignItems="center" justify="center">
                                <Button
                                    className="xtest_submit_button"
                                    color="primary"
                                    id="pay"
                                    size="large"
                                    type="submit"
                                    variant="contained"
                                    onClick={handlePayClick}
                                    disabled={paymentLoading}
                                >
                                    {paymentLoading ? <CircularProgress size={20} /> : t("summary:payment.pay")}
                                </Button>
                            </Grid>
                            {showPaymentFailedError && (
                                <Grid item container alignItems="center" justify="center">
                                    <Typography color="error" variant="body1">
                                        {t("summary:payment.paymentFailed")}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </HandleErrorAndLoading>
        </Card>
    );
};
